<script setup>
import { computed, h, inject, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { formatTimeTranslated } from 'dashboard/composables/useReportMetrics';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import CsatEmptyState from './components/CsatEmptyState.vue';
import ReportSummaryMetrics from './components/ReportSummaryMetrics.vue';
import Table from 'dashboard/components/table/Table.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();
const columnHelper = createColumnHelper();
const reportFilters = inject('contactReportFilters', ref({ from: 0, to: 0 }));

const hasFetched = ref(false);

const report = useMapGetter('summaryReports/getContactSummaryReports');
const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(
  () => !hasFetched.value || uiFlags.value.isFetchingContactSummaryReports
);

const isCompanyRanking = computed(
  () => reportFilters.value?.ranking !== 'contacts'
);
const companies = computed(() => report.value?.companies || []);
const looseContacts = computed(() => report.value?.contacts || []);
const rows = computed(() =>
  isCompanyRanking.value ? companies.value : looseContacts.value
);
const operation = computed(() => report.value?.operation || {});

const MEDALS = ['🥇', '🥈', '🥉'];

const rowDisplayName = row => {
  if (isCompanyRanking.value) {
    return row.name || row.domain || `#${row.id}`;
  }
  return row.name || row.email || row.phoneNumber || `#${row.id}`;
};

const rowRoute = row => {
  if (isCompanyRanking.value) {
    return {
      name: 'contact_reports_show',
      params: { companyId: row.id },
    };
  }
  return {
    name: 'contact_profile_dashboard',
    params: { contactId: row.id },
  };
};

const renderCount = value =>
  value == null ? '--' : Number(value).toLocaleString('pt-BR');

const renderAvgTime = value => (value ? formatTimeTranslated(value) : '--');

const topThree = computed(() => rows.value.slice(0, 3));
const podium = computed(() => {
  const [first, second, third] = topThree.value;
  return [
    second && { row: second, medal: MEDALS[1], position: 2 },
    first && { row: first, medal: MEDALS[0], position: 1 },
    third && { row: third, medal: MEDALS[2], position: 3 },
  ].filter(Boolean);
});

const tableData = computed(() =>
  rows.value.map((row, index) => ({
    ...row,
    rank: index + 1,
    displayName: rowDisplayName(row),
  }))
);

const operationItems = computed(() => [
  {
    label: t('CONTACT_REPORTS.METRICS.COMPANIES'),
    value: renderCount(operation.value.companiesCount),
    info: t('CONTACT_REPORTS.METRICS.COMPANIES_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.CONTACTS'),
    value: renderCount(operation.value.contactsCount),
    info: t('CONTACT_REPORTS.METRICS.CONTACTS_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.ENGAGEMENTS'),
    value: renderCount(operation.value.conversationsCount),
    info: t('CONTACT_REPORTS.METRICS.ENGAGEMENTS_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.MESSAGES'),
    value: renderCount(operation.value.messagesCount),
    info: t('CONTACT_REPORTS.METRICS.MESSAGES_INFO'),
  },
  {
    label: t('CONTACT_REPORTS.METRICS.AVG_HANDLING'),
    value: renderAvgTime(operation.value.avgResolutionTime),
    info: t('CONTACT_REPORTS.METRICS.AVG_HANDLING_INFO'),
  },
]);

const subjectCell = cellProps => {
  const row = cellProps.row.original;
  const subtitle = isCompanyRanking.value ? row.domain : row.email;
  return h(
    RouterLink,
    {
      to: rowRoute(row),
      class: 'flex items-center gap-2 min-w-0 text-n-slate-12 hover:underline',
      onClick: event => event.stopPropagation(),
    },
    {
      default: () => [
        h(Thumbnail, {
          username: row.displayName,
          size: '24px',
        }),
        h('span', { class: 'flex flex-col min-w-0' }, [
          h('span', { class: 'truncate font-medium' }, row.displayName),
          subtitle
            ? h('span', { class: 'truncate text-xs text-n-slate-11' }, subtitle)
            : null,
        ]),
      ],
    }
  );
};

const columns = computed(() => [
  columnHelper.accessor('rank', {
    header: t('CONTACT_REPORTS.TABLE.RANK'),
    size: 64,
    enableSorting: false,
    cell: cellProps =>
      h('span', { class: 'text-n-slate-11 font-medium' }, cellProps.getValue()),
  }),
  columnHelper.accessor('displayName', {
    header: isCompanyRanking.value
      ? t('CONTACT_REPORTS.TABLE.COMPANY')
      : t('CONTACT_REPORTS.TABLE.CONTACT'),
    size: 280,
    cell: subjectCell,
  }),
  ...(isCompanyRanking.value
    ? [
        columnHelper.accessor('contactsCount', {
          header: t('CONTACT_REPORTS.TABLE.CONTACTS'),
          size: 120,
          cell: cellProps => renderCount(cellProps.getValue()),
        }),
      ]
    : []),
  columnHelper.accessor('conversationsCount', {
    header: t('CONTACT_REPORTS.TABLE.ENGAGEMENTS'),
    size: 140,
    cell: cellProps => renderCount(cellProps.getValue()),
  }),
  columnHelper.accessor('messagesCount', {
    header: t('CONTACT_REPORTS.TABLE.MESSAGES'),
    size: 140,
    cell: cellProps => renderCount(cellProps.getValue()),
  }),
  columnHelper.accessor('totalResolutionTime', {
    header: t('CONTACT_REPORTS.TABLE.TOTAL_HANDLING_TIME'),
    size: 200,
    cell: cellProps => renderAvgTime(cellProps.getValue()),
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: t('CONTACT_REPORTS.TABLE.AVG_HANDLING_TIME'),
    size: 200,
    cell: cellProps => renderAvgTime(cellProps.getValue()),
  }),
]);

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
});

const fetchReports = async () => {
  const current = reportFilters.value;
  if (!current?.from || !current?.to) return;

  hasFetched.value = false;
  await store.dispatch('summaryReports/fetchContactSummaryReports', {
    since: current.from,
    until: current.to,
    businessHours: current.businessHours,
    inboxId: current.inboxId,
    teamId: current.teamId,
    agentIds: current.agentIds,
    ranking: current.ranking,
  });
  hasFetched.value = true;
};

watch(reportFilters, fetchReports, { deep: true, immediate: true });
</script>

<template>
  <div
    v-if="isLoading"
    class="flex items-center justify-center gap-2 py-16 text-n-slate-11"
  >
    <Spinner />
    <span>{{
      isCompanyRanking
        ? $t('CONTACT_REPORTS.LOADING')
        : $t('CONTACT_REPORTS.LOADING_CONTACTS')
    }}</span>
  </div>

  <template v-else-if="tableData.length">
    <div
      class="mt-6 flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-6"
    >
      <div class="flex flex-col gap-1">
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{ $t('CONTACT_REPORTS.OPERATION.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('CONTACT_REPORTS.OPERATION.DESCRIPTION') }}
        </p>
      </div>
      <ReportSummaryMetrics :items="operationItems" />
    </div>

    <div
      v-if="podium.length"
      class="mt-6 flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-6"
    >
      <div class="flex flex-col gap-1 text-center">
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{
            isCompanyRanking
              ? $t('CONTACT_REPORTS.RANKING.TITLE')
              : $t('CONTACT_REPORTS.RANKING.TITLE_CONTACTS')
          }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-11">
          {{
            isCompanyRanking
              ? $t('CONTACT_REPORTS.RANKING.DESCRIPTION')
              : $t('CONTACT_REPORTS.RANKING.DESCRIPTION_CONTACTS')
          }}
        </p>
      </div>

      <div class="flex items-end justify-center gap-4">
        <div
          v-for="entry in podium"
          :key="entry.row.id"
          class="flex w-44 flex-col items-center gap-2 rounded-xl border bg-n-solid-1 p-4 text-center"
          :class="
            entry.position === 1
              ? 'border-n-brand shadow-md -translate-y-2'
              : 'border-n-weak'
          "
        >
          <span class="text-2xl leading-none">{{ entry.medal }}</span>
          <Thumbnail :username="rowDisplayName(entry.row)" size="40px" />
          <RouterLink
            :to="rowRoute(entry.row)"
            class="text-sm font-medium text-n-slate-12 hover:underline"
          >
            {{ rowDisplayName(entry.row) }}
          </RouterLink>
          <span class="text-sm font-semibold text-n-blue-text">
            {{
              $t('CONTACT_REPORTS.RANKING.CONVERSATIONS', {
                count: entry.row.conversationsCount,
              })
            }}
          </span>
          <span v-if="isCompanyRanking" class="text-xs text-n-slate-11">
            {{
              $t('CONTACT_REPORTS.RANKING.CONTACTS', {
                count: entry.row.contactsCount,
              })
            }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="mt-5 flex-1 overflow-auto px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <Table
        :table="table"
        :row-route="isCompanyRanking ? rowRoute : undefined"
      />
    </div>
  </template>

  <CsatEmptyState
    v-else
    :title="
      isCompanyRanking
        ? $t('CONTACT_REPORTS.NO_RECORDS')
        : $t('CONTACT_REPORTS.NO_CONTACTS')
    "
    :description="$t('CONTACT_REPORTS.NO_RECORDS_DESCRIPTION')"
    :icon="isCompanyRanking ? 'i-lucide-building-2' : 'i-lucide-users'"
  />
</template>
